import { useEffect, useState } from "react";
import { Typography, Button, Grid, MenuItem, Select } from "@mui/material";
import { tss } from "tss-react/mui";
import { Tune } from "@mui/icons-material";
import { ViewerActions } from "actions";
import { handleClick } from "actions/ViewerTools";
import SliceContainer, { SliceHeader } from "panels/SliceContainer";
import { SliderInput } from "components";
import { voxelfarm } from "constants/viewer";
import { SliceSide, SliceFront, SliceHor } from "assets/images/index";
import commonStyles, { panelWidth } from "styles/common";
import CrossSectionBounds from "events/CrossSectionBounds";
import EventsEmitter from "events/EventsEmitter";
import ViewerEvents from "events/ViewerEvents";
import { alpha } from '@mui/material/styles';

const useStyles = tss.create(({theme}) => ({
    root: {
        minHeight: 30,
    },
    frontSlide: {
        position: "relative",
        width: 260,
        padding: 10,
    },
    frontSlideTop: {
        borderBottom: "solid 1px #E6E6E6",
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        backgroundSize: "16px 16px",
        verticalAlign: "middle",
        display: "inline-block",
        marginRight: 8,
    },
    title: {
        display: "inline-block",
        position: "relative",
        fontWeight: 600,
        left: 5,
    },
    button: {
        width: 30,
        height: 30,
        minWidth: 30,
    },
    frontSlideBottom: {
        position: "relative",
        verticalAlign: "middle",
        display: "table-cell",
        height: 30,
    },
    stepIncrementButton: {
        position: "relative",
        left: 68,
    },
    stepIncrementLabel: {
        display: "inline-block",
        fontSize: 14,
    },
    stepIncrement: {
        height: "100%",
        minHeight: 50,
        marginBottom: 10,
    },
    stepIncrementRow: {
        height: 40,
        position: "relative",
    },
    select: {
        width: "100%",
        height: 37,
        position: "relative",
        top: 10,
        fontSize: 14,
        paddingLeft: 10,
    },
    numberInput: {
        fontSize: 14,
        display: "inline-block",
        width: 60,
        outline: 0,
        border: 0,
        borderBottom: `solid 1px ${theme.palette.divider}`,
        textAlign: "right",
        padding: "1px 5px",
        background: alpha(theme.palette.background.default, 0.15),
        color: theme.palette.text.secondary,
        paddingRight: '8px'
    },
    numberHint: {
        textAlign: "center",
    },
    menuitem: {
        fontSize: 14,
    },
    menuItemName: {
        display: "block",
        fontSize: 11,
        position: "relative",
        height: 14,
        lineHeight: "14px",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: 186,
    },
    menuItemBlock: {
        display: "block",
        height: 20,
        lineHeight: "20px",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: 186,
    },
    rightDiv: {
        display: "inline-block",
        position: "absolute",
        right: 5,
        top: 0,
    },
    stepIncrementBottom: {
        display: "table",
        width: 187,
    },
}));

const MenuProps = {
    PaperProps: {
        style: {
            width: 180,
            borderRadius: 3,
        },
    },
};

const round = (num: number) => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
};

const presetTypeTranslator: any = {
    1: 11,
    2: 3,
    3: 4,
    4: 2,
    5: 0,
    6: 6,
    7: 7,
    8: 5,
    9: 1,
    10: 9,
    11: 10,
    12: 8,
};

function getCrossSection(presetType: number, crossSectionBounds: any) {
    if (presetType === 2 || presetType === 6 || presetType === 10) {
        return {
            plane: "x",
            values: crossSectionBounds["xPosition"],
            dipValues: crossSectionBounds["xDip"],
            dipAzimuthValues: crossSectionBounds["xDipAzimuth"],
        };
    } else if (presetType === 3 || presetType === 7 || presetType === 11) {
        return {
            plane: "y",
            values: crossSectionBounds["yPosition"],
            dipValues: crossSectionBounds["yDip"],
            dipAzimuthValues: crossSectionBounds["yDipAzimuth"],
        };
    } else if (presetType === 4 || presetType === 8 || presetType === 12) {
        return {
            plane: "z",
            values: crossSectionBounds["zPosition"],
            dipValues: crossSectionBounds["zDip"],
            dipAzimuthValues: crossSectionBounds["zDipAzimuth"],
        };
    } else if (presetType === 1) {
        return {
            plane: "w",
            values: crossSectionBounds["wPosition"],
            dipValues: crossSectionBounds["wDip"],
            dipAzimuthValues: crossSectionBounds["wDipAzimuth"],
        };
    }
    return { plane: "", values: {}, dipValues: {}, dipAzimuthValues: {} };
}

type IStepProps = {
    handleChange: (event: any, name: string, value: any) => void;
    updateParent: () => void;
    crossSectionBounds: any;
    plane: string;
    settings: any;
};

function StepIncrement(props: IStepProps) {
    const { classes } = useStyles();
    const { handleChange, updateParent, plane, settings } = props;
    let idx = 0,
        count = 0,
        defaultValue = 0;
    const viewBounds = ViewerActions.getViewBounds();

    Object.values(viewBounds).forEach((bounds: any) => {
        return (
            bounds.blocks &&
            bounds.blocks.forEach((block: any, index: number) => {
                let layerId: string = bounds.id;
                idx++;
                if (layerId === viewBounds.selectedLayerId && index === viewBounds.selectedIndex) {
                    defaultValue = idx;
                }
            })
        );
    });

    const [value, setValue] = useState(defaultValue);
    const selectIndex = viewBounds && viewBounds.selectedIndex > -1 ? 1 : 0;
    const [stepType, setStepType] = useState(selectIndex);
    const bounds = [3, 3, 3];

    const handleCustomStepIncrement = (block: any) => {
        handleClick({
            cmd: "updateCustomCrossSectionStepIncrement",
            param: {
                block: block,
            },
        });
        updateParent();
    };

    const handleStepIncrement = (layerId: string, aabb: any, block: any) => {
        handleClick({
            cmd: "updateCrossSectionStepIncrement",
            param: {
                layerId: layerId,
                aabb: aabb,
                block: block,
            },
        });
        updateParent();
    };

    const updateValue = (newValue: number, layerIndex: number, value: number) => {
        if (newValue !== 0) {
            settings["xPosition"].step = viewBounds[layerIndex].blocks[value][0];
            settings["yPosition"].step = viewBounds[layerIndex].blocks[value][1];
            settings["zPosition"].step = viewBounds[layerIndex].blocks[value][2];
            viewBounds.selectedLayerId = viewBounds[layerIndex].layerId;
            viewBounds.selectedIndex = value;
        } else {
            settings["xPosition"].step = bounds[0];
            settings["yPosition"].step = bounds[1];
            settings["zPosition"].step = bounds[2];
            viewBounds.selectedIndex = -1;
            viewBounds.selectedLayerId = "";
        }
        CrossSectionBounds.setInitialValue("xPosition", "step", settings["xPosition"].step);
        CrossSectionBounds.setInitialValue("yPosition", "step", settings["yPosition"].step);
        CrossSectionBounds.setInitialValue("zPosition", "step", settings["zPosition"].step);
        if (newValue !== value) {
            setValue(newValue);
        }
        setStepType(newValue);
    };

    return (
        <div className={classes.stepIncrement}>
            <div>
                <Select className={classes.select} MenuProps={MenuProps} defaultValue={value}>
                    <MenuItem
                        className={classes.menuitem}
                        value={0}
                        onClick={(e: any) => {
                            updateValue(0, 0, 0);
                            handleCustomStepIncrement(bounds);
                        }}
                    >
                        Custom
                    </MenuItem>
                    {Object.values(viewBounds).map((bounds: any, layerIndex: number) => {
                        let name = bounds.name;
                        let layerId: string = bounds.id;
                        return (
                            bounds.blocks &&
                            bounds.blocks.map((block: any, index: number) => {
                                count++;
                                return (
                                    <MenuItem
                                        className={classes.menuitem}
                                        key={"bounds-" + index}
                                        value={count}
                                        selected={
                                            layerId === viewBounds.selectedLayerId && index === viewBounds.selectedIndex
                                        }
                                        onClick={(e: any) => {
                                            updateValue(count, layerIndex, index);
                                            handleStepIncrement(layerId, viewBounds[layerIndex].aabb, block);
                                        }}
                                    >
                                        <div>
                                            <div className={classes.menuItemBlock}>
                                                {block[0] + " x " + block[1] + " x " + block[2]}
                                            </div>
                                            <div className={classes.menuItemName}>{name}</div>
                                        </div>
                                    </MenuItem>
                                );
                            })
                        );
                    })}
                </Select>
            </div>
            {stepType === 0 ? (
                plane === "all" ? (
                    <div style={{ position: "relative", top: 30, paddingBottom: 20 }}>
                        <div className={classes.stepIncrementRow}>
                            <Typography className={classes.stepIncrementLabel}>Step Increment X</Typography>
                            <Grid item className={classes.rightDiv}>
                                <input
                                    type="number"
                                    defaultValue={round(settings.xPosition.step)}
                                    className={classes.numberInput}
                                    onChange={(evt) => {
                                        handleChange(evt, "stepIncrementX", evt.target.value);
                                    }}
                                />
                            </Grid>
                        </div>
                        <div className={classes.stepIncrementRow}>
                            <Typography className={classes.stepIncrementLabel}>Step Increment Y</Typography>
                            <Grid item className={classes.rightDiv}>
                                <input
                                    type="number"
                                    defaultValue={round(settings.yPosition.step)}
                                    className={classes.numberInput}
                                    onChange={(evt) => {
                                        handleChange(evt, "stepIncrementY", evt.target.value);
                                    }}
                                />
                            </Grid>
                        </div>
                        <div className={classes.stepIncrementRow}>
                            <Typography className={classes.stepIncrementLabel}>Step Increment Z</Typography>
                            <Grid item className={classes.rightDiv}>
                                <input
                                    type="number"
                                    defaultValue={round(settings.zPosition.step)}
                                    className={classes.numberInput}
                                    onChange={(evt) => {
                                        handleChange(evt, "stepIncrementZ", evt.target.value);
                                    }}
                                />
                            </Grid>
                        </div>
                    </div>
                ) : (
                    <div style={{ position: "relative", top: 30, paddingBottom: 20 }}>
                        <div className={classes.stepIncrementRow}>
                            <Typography className={classes.stepIncrementLabel}>
                                Step Increment {plane.toUpperCase()}
                            </Typography>
                            <Grid item className={classes.rightDiv}>
                                <input
                                    type="number"
                                    defaultValue={round(settings[plane + "Position"].step)}
                                    className={classes.numberInput}
                                    onChange={(evt) => {
                                        handleChange(evt, "stepIncrement" + plane.toUpperCase(), evt.target.value);
                                    }}
                                />
                            </Grid>
                        </div>
                    </div>
                )
            ) : (
                ""
            )}
        </div>
    );
}

type ISliceProps = {
    preset: number;
};

function FrontSlice(props: ISliceProps) {
    const { classes } = useStyles();
    const { preset } = props;
    var crossSection = CrossSectionBounds.getCrossSectionBounds();
    const initialPosition = getCrossSection(preset, crossSection);
    const plane = initialPosition.plane;
    const currentPosition = initialPosition.values;
    const currentDip = initialPosition.dipValues;
    const currentDipAzimuth = initialPosition.dipAzimuthValues;
    const settings = { ...crossSection };
    const [stepIncrement, showStepIncrement] = useState(false);
    const [update, setUpdate] = useState(false);

    const changeSlicePosition = (direction: number) => {
        var value: number = currentPosition.value;
        if (direction === -1) {
            value -= currentPosition.step;
        } else {
            value += currentPosition.step;
        }
        handleChange(null, "position", value);
        setUpdate((prevVal) => !prevVal);
    };

    useEffect(() => {
        const onKeyDown = (event: any) => {
            console.log("event:", event);
            if (event.srcElement.tagName === "INPUT" || event.srcElement.tagName === "TEXTAREA") {
                return;
            }
            switch (event.keyCode) {
                case 188: //comma
                    changeSlicePosition(-1);
                    break;
                case 190: //period
                    changeSlicePosition(1);
                    break;
            }
        };
        document.addEventListener("keydown", onKeyDown, false);
        return () => {
            document.removeEventListener("keydown", onKeyDown, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);
    //console.log('plane:', plane, '|currentPosition:', currentPosition);
    const compositeMeta = ViewerEvents.compositeMeta;

    const isBlockModel = compositeMeta
        ? Boolean(compositeMeta.components.find((component: any) => component.type === voxelfarm.META_TYPE_BLOCKTREE))
        : false;
    const isDrillHoles = compositeMeta
        ? Boolean(compositeMeta.components.find((component: any) => component.type === voxelfarm.META_TYPE_VECTOR))
        : false;

    const toggleStepIncrement = (evt: any) => {
        showStepIncrement(!stepIncrement);
    };

    const updateParent = () => {
        setUpdate(!update);
    };

    const handleChange = (event: any, name: string, value: any) => {
        let newSettings: any = { ...settings };
        if (name === "position") {
            currentPosition.value = value;
            newSettings[plane + "Position"].value = value;
        } else if (name.indexOf("stepIncrement") !== -1) {
            newSettings[plane + "Position"].step = parseFloat(value);
            setUpdate(!update);
        } else if (name === "dip") {
            currentDip.value = value;
            newSettings[plane + "Dip"].value = value;
        } else if (name === "dipAzimuth") {
            currentDipAzimuth.value = value;
            newSettings[plane + "DipAzimuth"].value = value;
        } else {
            newSettings[name].value = value;
        }

        if (name === "position") {
            if (plane === "x") {
                handleClick({ cmd: "updateCrossSectionX", param: value });
            } else if (plane === "y") {
                handleClick({ cmd: "updateCrossSectionY", param: value });
            } else if (plane === "z") {
                const worldOrginZ = CrossSectionBounds.worldOriginMin[2];
                handleClick({ cmd: "updateCrossSectionZ", param: value - worldOrginZ });
            } //plane === "w"
            else {
                handleClick({ cmd: "updateCrossSectionW", param: value });
            }
        } else if (name === "width") {
            handleClick({ cmd: "updateCrossSectionWidth", param: value });
        } else if (name === "blockModelWidth") {
            handleClick({ cmd: "updateCrossSectionBlockModelWidth", param: value });
        } else if (name === "drillHolesWidth") {
            handleClick({ cmd: "updateCrossSectionDrillHolesWidth", param: value });
        } else if (name === "dip") {
            if (plane === "x") {
                handleClick({ cmd: "updateCrossSectionXDip", param: value });
            } else if (plane === "y") {
                handleClick({ cmd: "updateCrossSectionYDip", param: value });
            } else if (plane === "z") {
                handleClick({ cmd: "updateCrossSectionZDip", param: value });
            } //plane === "w"
            else {
                handleClick({ cmd: "updateCrossSectionWDip", param: value });
            }
        } else if (name === "dipAzimuth") {
            if (plane === "x") {
                handleClick({ cmd: "updateCrossSectionXDipAzimuth", param: value });
            } else if (plane === "y") {
                handleClick({ cmd: "updateCrossSectionYDipAzimuth", param: value });
            } else if (plane === "z") {
                handleClick({ cmd: "updateCrossSectionZDipAzimuth", param: value });
            } //plane === "w"
            else {
                handleClick({ cmd: "updateCrossSectionWDipAzimuth", param: value });
            }
        } else if (name.indexOf("stepIncrement") !== -1) {
            const newStep = [
                newSettings["xPosition"].step,
                newSettings["yPosition"].step,
                newSettings["zPosition"].step,
            ];
            handleClick({
                cmd: "updateCustomCrossSectionStepIncrement",
                param: {
                    block: newStep,
                },
            });
        }
    };

    const showWidth = preset === 2 || preset === 3 || preset === 4;
    if (preset !== 5 && preset !== 9) {
        return (
            <div className={classes.frontSlide}>
                <div className={classes.frontSlideTop}>
                    <SliderInput
                        name="position"
                        title="Set Position"
                        min={currentPosition.min}
                        max={currentPosition.max}
                        step={currentPosition.step}
                        value={currentPosition.value}
                        handleChange={handleChange}
                    />
                    {showWidth ? (
                        <div>
                            <SliderInput
                                name="width"
                                title="Set Section Width"
                                min={0}
                                max={1000}
                                step={3}
                                value={settings.width.value}
                                handleChange={handleChange}
                            />
                            {isBlockModel ? (
                                <SliderInput
                                    name="blockModelWidth"
                                    title="Set Block Model Width"
                                    min={0}
                                    max={1000}
                                    step={3}
                                    value={settings.blockModelWidth.value}
                                    handleChange={handleChange}
                                />
                            ) : (
                                ""
                            )}
                            {isDrillHoles ? (
                                <SliderInput
                                    name="drillHolesWidth"
                                    title="Set Drill Holes Width"
                                    min={0}
                                    max={1000}
                                    step={3}
                                    value={settings.drillHolesWidth.value}
                                    handleChange={handleChange}
                                />
                            ) : (
                                ""
                            )}
                        </div>
                    ) : (
                        ""
                    )}

                    <SliderInput
                        name="dip"
                        title="Set Dip Angle"
                        min={currentDip.min}
                        max={currentDip.max}
                        step={currentDip.step}
                        value={currentDip.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="dipAzimuth"
                        title="Set Azimuth Angle"
                        min={currentDipAzimuth.min}
                        max={currentDipAzimuth.max}
                        step={currentDipAzimuth.step}
                        value={currentDipAzimuth.value}
                        handleChange={handleChange}
                    />
                </div>
                <div className={classes.stepIncrementBottom}>
                    <div className={classes.frontSlideBottom}>
                        <Typography className={classes.stepIncrementLabel}>Set Step Increment </Typography>
                        <div className={classes.rightDiv}>
                            <Button
                                className={[classes.button, classes.stepIncrementButton].join(" ")}
                                onClick={toggleStepIncrement}
                                color={stepIncrement ? "primary" : "inherit"}
                            >
                                <Tune className={classes.icon} />
                            </Button>
                        </div>
                    </div>
                </div>
                {stepIncrement && plane !== "" ? (
                    <StepIncrement
                        handleChange={handleChange}
                        plane={plane}
                        settings={settings}
                        updateParent={updateParent}
                        crossSectionBounds={crossSection}
                        {...props}
                    />
                ) : (
                    ""
                )}
            </div>
        );
    }
    return <div />;
}

function AllSlice(props: ISliceProps) {
    const { classes } = useStyles();
    const { preset } = props;
    const crossSection = CrossSectionBounds.getCrossSectionBounds();
    //console.log("AllSlice.crossSection:", crossSection);
    const settings = { ...crossSection };
    const compositeMeta = ViewerEvents.compositeMeta;

    const isBlockModel = compositeMeta
        ? Boolean(compositeMeta.components.find((component: any) => component.type === voxelfarm.META_TYPE_BLOCKTREE))
        : false;
    const isDrillHoles = compositeMeta
        ? Boolean(compositeMeta.components.find((component: any) => component.type === voxelfarm.META_TYPE_VECTOR))
        : false;
    const [stepIncrement, showStepIncrement] = useState(false);
    const [update, setUpdate] = useState(false);

    const toggleStepIncrement = (evt: any) => {
        showStepIncrement(!stepIncrement);
    };

    const updateParent = () => {
        setUpdate(!update);
    };

    const handleChange = (event: any, name: string, value: any) => {
        let newSettings = { ...settings };

        if (name.indexOf("stepIncrement") !== -1) {
            const plane = name.replace("stepIncrement", "").toLowerCase();
            newSettings[plane + "Position"].step = parseFloat(value);
            setUpdate(!update);
        } else {
            newSettings[name].value = value;
        }

        //console.log("newSettings:", newSettings);
        if (name === "xPosition") {
            handleClick({ cmd: "updateCrossSectionX", param: value });
        } else if (name === "yPosition") {
            handleClick({ cmd: "updateCrossSectionY", param: value });
        } else if (name === "zPosition") {
            const worldOrginZ = CrossSectionBounds.worldOriginMin[2];
            handleClick({ cmd: "updateCrossSectionZ", param: value - worldOrginZ });
        }
        //Commented because this is the mode where we show the orthogonal planes, not the custom one
        //else if (name === "wPosition")
        //{
        //    handleClick({cmd:"updateCrossSectionW", param: value});
        //}
        else if (name === "width") {
            handleClick({ cmd: "updateCrossSectionWidth", param: value });
        } else if (name === "blockModelWidth") {
            handleClick({ cmd: "updateCrossSectionBlockModelWidth", param: value });
        } else if (name === "drillHolesWidth") {
            handleClick({ cmd: "updateCrossSectionDrillHolesWidth", param: value });
        } else if (name === "xDip") {
            handleClick({ cmd: "updateCrossSectionXDip", param: value });
        } else if (name === "yDip") {
            handleClick({ cmd: "updateCrossSectionYDip", param: value });
        } else if (name === "zDip") {
            handleClick({ cmd: "updateCrossSectionZDip", param: value });
        } else if (name === "xDipAzimuth") {
            handleClick({ cmd: "updateCrossSectionXDipAzimuth", param: value });
        } else if (name === "yDipAzimuth") {
            handleClick({ cmd: "updateCrossSectionYDipAzimuth", param: value });
        } else if (name === "zDipAzimuth") {
            handleClick({ cmd: "updateCrossSectionZDipAzimuth", param: value });
        }
    };

    if (preset === 9) {
        return (
            <div className={classes.frontSlide}>
                <div className={classes.frontSlideTop}>
                    <SliderInput
                        name="xPosition"
                        title="X Position"
                        icon={<SliceSide className={classes.icon} />}
                        min={settings.xPosition.min}
                        max={settings.xPosition.max}
                        step={settings.xPosition.step}
                        value={settings.xPosition.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="yPosition"
                        title="Y Position"
                        icon={<SliceFront className={classes.icon} />}
                        min={settings.yPosition.min}
                        max={settings.yPosition.max}
                        step={settings.yPosition.step}
                        value={settings.yPosition.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="zPosition"
                        title="Z Position"
                        icon={<SliceHor className={classes.icon} />}
                        min={settings.zPosition.min}
                        max={settings.zPosition.max}
                        step={settings.zPosition.step}
                        value={settings.zPosition.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="width"
                        title="Set Section Width"
                        min={0}
                        max={1000}
                        step={3}
                        value={settings.width.value}
                        handleChange={handleChange}
                    />
                    {isBlockModel ? (
                        <SliderInput
                            name="blockModelWidth"
                            title="Set Block Model Width"
                            min={0}
                            max={1000}
                            step={3}
                            value={settings.blockModelWidth.value}
                            handleChange={handleChange}
                        />
                    ) : (
                        ""
                    )}
                    {isDrillHoles ? (
                        <SliderInput
                            name="drillHolesWidth"
                            title="Set Drill Holes Width"
                            min={0}
                            max={1000}
                            step={3}
                            value={settings.drillHolesWidth.value}
                            handleChange={handleChange}
                        />
                    ) : (
                        ""
                    )}

                    <SliderInput
                        name="xDip"
                        title="X Dip Angle"
                        min={settings.xDip.min}
                        max={settings.xDip.max}
                        step={settings.xDip.step}
                        value={settings.xDip.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="xDipAzimuth"
                        title="X Azimuth Angle"
                        min={settings.xDipAzimuth.min}
                        max={settings.xDipAzimuth.max}
                        step={settings.xDipAzimuth.step}
                        value={settings.xDipAzimuth.value}
                        handleChange={handleChange}
                    />

                    <SliderInput
                        name="yDip"
                        title="Y Dip Angle"
                        min={settings.yDip.min}
                        max={settings.yDip.max}
                        step={settings.yDip.step}
                        value={settings.yDip.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="yDipAzimuth"
                        title="Y Azimuth Angle"
                        min={settings.yDipAzimuth.min}
                        max={settings.yDipAzimuth.max}
                        step={settings.yDipAzimuth.step}
                        value={settings.yDipAzimuth.value}
                        handleChange={handleChange}
                    />

                    <SliderInput
                        name="zDip"
                        title="Z Dip Angle"
                        min={settings.zDip.min}
                        max={settings.zDip.max}
                        step={settings.zDip.step}
                        value={settings.zDip.value}
                        handleChange={handleChange}
                    />
                    <SliderInput
                        name="zDipAzimuth"
                        title="Z Azimuth Angle"
                        min={settings.zDipAzimuth.min}
                        max={settings.zDipAzimuth.max}
                        step={settings.zDipAzimuth.step}
                        value={settings.zDipAzimuth.value}
                        handleChange={handleChange}
                    />
                </div>
                <div style={{ display: "table" }}>
                    <div className={classes.frontSlideBottom}>
                        <Typography className={classes.stepIncrementLabel}>Set Step Increment </Typography>
                        <Button
                            className={[classes.button, classes.stepIncrementButton].join(" ")}
                            onClick={toggleStepIncrement}
                            color={stepIncrement ? "primary" : "inherit"}
                        >
                            <Tune className={classes.icon} />
                        </Button>
                    </div>
                </div>
                {stepIncrement ? (
                    <StepIncrement
                        handleChange={handleChange}
                        plane={"all"}
                        settings={settings}
                        updateParent={updateParent}
                        crossSectionBounds={crossSection}
                        {...props}
                    />
                ) : (
                    ""
                )}
            </div>
        );
    }
    return <div />;
}

type IProps = {
    show: boolean;
};

export default function SlicePanel(props: IProps) {
    const { classes: common } = commonStyles();
    const { classes, cx } = useStyles();
    const { show } = props;
    const [update, setUpdate] = useState(false);
    const [preset, setPreset] = useState(5);
    const [collapse, setCollapse] = useState(false);
    const [presetSelected, selectPreset] = useState(5);

    useEffect(() => {
        const updateBounds = () => {
            const viewBounds = ViewerActions.getViewBounds();
            //console.log('viewBounds:', viewBounds);
            const boundsIndex = (viewBounds && viewBounds.selectedIndex) || 0;
            const layerIndex =
                viewBounds &&
                viewBounds.findIndex((viewBound: any) => viewBound.layerId === viewBounds.selectedLayerId);
            if (layerIndex > -1 && boundsIndex > -1) {
                const boundsValue = viewBounds[layerIndex].blocks[boundsIndex];
                if (boundsValue) {
                    CrossSectionBounds.setInitialValue("xPosition", "step", boundsValue[0]);
                    CrossSectionBounds.setInitialValue("yPosition", "step", boundsValue[1]);
                    CrossSectionBounds.setInitialValue("zPosition", "step", boundsValue[2]);
                }
            }
        };
        const updateSliders = () => {
            //console.log("updateSliders");
            setUpdate((prevUpdate) => !prevUpdate);
        };
        const handleClose = () => {
            setPreset(5);
            selectPreset(5);
            setCollapse(false);
            updateCrossSection(presetTypeTranslator[5]);
            EventsEmitter.emit("updateCrossSection", presetTypeTranslator[5]);
        };
        EventsEmitter.on("updateSliders", updateSliders);
        updateBounds();
        return () => {
            EventsEmitter.removeEventListener("updateSliders", updateSliders);
            handleClose();
        };
    }, []);

    if (!show) {
        return <div />;
    }
    const toggleHeader = () => {
        let newcollapse = !collapse;
        setCollapse(newcollapse);
    };
    var updateCrossSection = (presetType: number) => {
        handleClick({ cmd: "updateCrossSectionPresetType", param: presetType });
    };
    const handleCurrentClick = (preset: number) => {
        setPreset(preset);
        if (preset !== 5) {
            setCollapse(true);
        }
        selectPreset(preset);
        updateCrossSection(presetTypeTranslator[preset]);
        EventsEmitter.emit("updateCrossSection", presetTypeTranslator[preset]);
    };

    return (
        <div className={cx(common.middleCenter, classes.root)}>
            <div
                style={{
                    textAlign: "left",
                    width: panelWidth - 20,
                    userSelect: update ? "none" : "none",
                }}
            >
                <SliceHeader preset={preset} collapse={collapse} toggleHeader={toggleHeader} />
                {!collapse ? <SliceContainer presetSelected={presetSelected} handleClick={handleCurrentClick} /> : ""}
                <FrontSlice preset={preset} {...props} />
                <AllSlice preset={preset} {...props} />
            </div>
        </div>
    );
}
